import { useEffect, useMemo, useState } from 'react';
import IconPlus from '../icons/IconPlus';
import IconMinus from '../icons/IconMinus';

interface NumberInputBaseProps {
  id: string;
  value: number;
  min: number;
  max: number;
  step: number;
  onChange: (value: number) => void;
  onIncrement: (value: number) => void;
  onDecrement: (value: number) => void;
}

function NumberInputBase({ id, value, min, max, step, onChange, onIncrement, onDecrement }: NumberInputBaseProps) {
  const [inputValue, setInputValue] = useState(value);

  useEffect(() => {
    setInputValue(value);
  }, [value]);

  const fixedValue = useMemo(() => {
    if (step === 0.1) return 1;
    if (step === 0.01) return 2;
    return 0;
  }, [step]);

  const clampValue = (val: number): number => Number(Math.max(min, Math.min(max, val)).toFixed(fixedValue));

  const handleDecrement = (): void => {
    const newValue = clampValue(value - step);
    setInputValue(newValue);
    onDecrement(newValue);
  };

  const handleIncrement = (): void => {
    const newValue = clampValue(value + step);
    setInputValue(newValue);
    onIncrement(newValue);
  };

  const handleBlur = (): void => {
    const clampedValue = clampValue(inputValue);
    setInputValue(clampedValue);
    onChange(clampedValue);
  };

  return (
    <div className="flex justify-center items-center !min-w-[102px] !h-[40px] bg-white">
      <button
        onClick={handleDecrement}
        className="w-[24px] h-full flex justify-center items-center text-dark-text hover:text-primary-color"
        aria-label="Decrease value"
      >
        <IconMinus />
      </button>
      <input
        className="!flex-1 !h-full !border-1 !border-middle-gray !font-cond-400-14 !text-center"
        id={id}
        type="number"
        value={inputValue}
        min={min}
        max={max}
        step={step}
        onChange={(e) => setInputValue(Number(e.target.value))}
        onBlur={handleBlur}
      />
      <button
        onClick={handleIncrement}
        className="w-[24px] h-full flex justify-center items-center text-dark-text hover:text-primary-color"
        aria-label="Increase value"
      >
        <IconPlus />
      </button>
    </div>
  );
}

export default NumberInputBase;
